import React, { memo, useState, useContext, useEffect, Fragment, useRef, useCallback } from 'react'
import { useNavigate, useSearchParams, Link } from 'react-router-dom'
import { AuthContext } from '../../context/auth-context'
import { GuideContext } from '../../context/guide-context'
import { gql, useQuery, useMutation } from '@apollo/client'
import { USER_FIELDS } from '../../graphql/user-fragments'
import { COMPLETE_DRAFT_CAMPAIGN, CREATE_CAMPAIGN_WITH_SUBSCRIPTIONS } from '../../graphql/subscription-queries'
import Card from '../../components/common/Card'
import CampaignForm from '../../components/campaigns/CampaignForm'
import SubscriptionStep from './AddCampaign/SubscriptionStep'
import { showNotification } from '../../utils/notifications'
import { ToastContainer } from 'react-toastify'
import { useOnboardingTour } from '../../utils/onboarding-tour-hook'
import { useActionLock } from '@/hooks/useActionLock'
import { Button } from '@/components/ui/button'
import { PlusIcon, SubscriptionsIcon } from '@/components/icons/new'
import { SUBSCRIPTION_PLANS } from '@/pages/users/Subscriptions/config'
import { typeToConfigKey } from '@/pages/users/Subscriptions/planResolver'
import { overviewDraftInputOf } from '@/hooks/useLicenseOverviewStep'

const GET_USER = gql`
    ${USER_FIELDS}
    query Users($where: UserWhere) {
        users(where: $where) {
            ...UserFields
        }
    }
`

const campaignInputFromData = (data) => ({
    name: data.name || 'Untitled License',
    userStatus: data.userStatus || 'ACTIVE',
    systemStatus: 'DRAFT',
    notes: data.notes || '',
    purpose: data.purpose || '',
    geolocation: data.geolocation?.latitude ? data.geolocation : { latitude: 0, longitude: 0 },
    address: data.address || '',
    radius: parseInt(data.radius) || 1,
    keywords: JSON.stringify(data.keywords?.length ? data.keywords : []),
    ...(data.licenseOverview ? { licenseOverview: overviewDraftInputOf(data.licenseOverview) } : {}),
    user: {
        ...(data.email && data.email.trim() !== '' ? { email: data.email } : {}),
        username: data.username || '',
        password: data.password || '',
        firstName: data.firstName || '',
        lastName: data.lastName || '',
        timeZoneOffset: data.timeZoneOffset || 0,
    },
})

// A failed subscription's `type` matches the GraphQL enum (e.g. "SUPPORT"), which this maps to the
// same UI title shown throughout the Subscriptions screen.
const serviceLabel = (type) => SUBSCRIPTION_PLANS.find((p) => p.id === typeToConfigKey[type])?.title ?? type

// Reads `?draft=` from the router so a change to the query string (not just the path) is followed —
// remounted via the `key` below whenever it changes, so every piece of wizard state starts fresh
// for the newly requested draft instead of carrying over stale values from the previous one.
//
// `recovery` is the one deliberate exception: when creating a new licence fails after the backend
// already created the draft behind it (reported via the error's `extensions.campaignId`), retrying
// the same "create" mutation would create a second draft and could charge a second licence. Instead
// the wizard is sent to complete that draft — held here, above the remount, so the error message and
// the plans/payment method the customer already chose survive the switch.
const AddCampaign = memo(() => {
    const [searchParams] = useSearchParams()
    const navigate = useNavigate()
    const draftCampaignId = searchParams.get('draft')
    const [recovery, setRecovery] = useState(null)

    const handleCreateFailedWithDraft = useCallback(
        ({ campaignId, message, subscriptionUi }) => {
            setRecovery({ forDraftId: campaignId, message, subscriptionUi })
            navigate(`/campaigns/new?draft=${encodeURIComponent(campaignId)}`, { replace: true })
        },
        [navigate],
    )

    const handleRecoveryConsumed = useCallback(() => setRecovery(null), [])

    const recoveryForThisDraft = recovery?.forDraftId && recovery.forDraftId === draftCampaignId ? recovery : null

    return (
        <AddCampaignInner
            key={draftCampaignId ?? 'new'}
            draftCampaignId={draftCampaignId}
            initialSubmitError={recoveryForThisDraft?.message ?? null}
            initialSubscriptionUi={recoveryForThisDraft?.subscriptionUi ?? null}
            onCreateFailedWithDraft={handleCreateFailedWithDraft}
            onRecoveryConsumed={handleRecoveryConsumed}
        />
    )
})

AddCampaign.displayName = 'AddCampaign'

const AddCampaignInner = memo(
    ({
        draftCampaignId,
        initialSubmitError = null,
        initialSubscriptionUi = null,
        onCreateFailedWithDraft,
        onRecoveryConsumed,
    }) => {
    // Completing an existing draft skips the campaign details step: those are already recorded on
    // the draft, and only a subscription is missing. Asking for them again would collect input this
    // screen does not send.
    const completingDraft = Boolean(draftCampaignId)

    const [step, setStep] = useState(completingDraft ? 2 : 1)
    const [subscriptionMounted, setSubscriptionMounted] = useState(completingDraft)
    const [subscriptionUi, setSubscriptionUi] = useState(initialSubscriptionUi)
    const [campaignSubmitAction, setCampaignSubmitAction] = useState(null)
    const [campaignCancelAction, setCampaignCancelAction] = useState(null)
    const [campaignSubmitDisabled, setCampaignSubmitDisabled] = useState(false)
    const [campaignCancelDisabled, setCampaignCancelDisabled] = useState(false)
    const [campaignSubmitLabel, setCampaignSubmitLabel] = useState('Next')
    const [subscriptionCanSubmit, setSubscriptionCanSubmit] = useState(false)
    const [subscriptionSubmitAction, setSubscriptionSubmitAction] = useState(null)
    const [updatedData, setUpdatedData] = useState({})
    const [errors, setError] = useState([])
    const [submitError, setSubmitError] = useState(initialSubmitError)
    const [partialSuccess, setPartialSuccess] = useState(null)
    const navigate = useNavigate()
    const { user } = useContext(AuthContext)
    const { guide } = useContext(GuideContext)
    const { focus } = useOnboardingTour()
    const guidedRef = useRef(false)

    const { data: userData } = useQuery(GET_USER, {
        variables: { where: { id: user.id } },
        fetchPolicy: 'cache-and-network',
    })
    const currentUser = userData?.users?.[0] ?? null
    const paymentMethodId = currentUser?.paymentMethodId ?? null

    // The licence itself is created either way — `failedSubscriptions` only lists the add-on
    // services (support/creatives) that could not be charged. That is a partial success, not a
    // failure: the customer keeps the licence and is pointed at Subscriptions to add them later.
    const handleCreationResult = useCallback(
        (created, successMessage) => {
            const failed = created?.failedSubscriptions ?? []
            if (failed.length > 0) {
                setPartialSuccess({
                    campaignId: created.id,
                    systemStatus: created.systemStatus,
                    failedSubscriptions: failed,
                })
                return
            }
            showNotification(successMessage, 'success', 4000)
            navigate('/campaigns', { state: { refetch: true } })
        },
        [navigate],
    )

    const [completeDraftCampaign, completeMutation] = useMutation(COMPLETE_DRAFT_CAMPAIGN, {
        onError(error) {
            const gqlError = error.graphQLErrors?.[0]
            const gqlMessage = gqlError?.message
            const netMessage = error.networkError?.message
            const campaignId = gqlError?.extensions?.campaignId
            if (campaignId) {
                showNotification(gqlMessage || 'Your license payment succeeded, but setup needs to be completed.', 'error', 8000)
                navigate(`/subscriptions?license=${encodeURIComponent(campaignId)}`)
                return
            }
            setSubmitError(gqlMessage || netMessage || error.message || 'Failed to complete license.')
        },
        onCompleted(result) {
            if (!result?.completeDraftCampaign) return
            handleCreationResult(result.completeDraftCampaign, 'License completed!')
        },
    })

    const [createCampaignWithSubscriptions, createMutation] = useMutation(CREATE_CAMPAIGN_WITH_SUBSCRIPTIONS, {
        onError(error) {
            const gqlError = error.graphQLErrors?.[0]
            const message = gqlError?.message || error.networkError?.message || error.message || 'Failed to create license.'

            // The licence step can fail after the backend has already created the draft behind it.
            // Retrying "create" here would make a second draft and could charge a second licence, so
            // the wizard switches to completing the one that already exists instead.
            const recoveredCampaignId = gqlError?.extensions?.campaignId
            if (recoveredCampaignId) {
                onCreateFailedWithDraft?.({ campaignId: recoveredCampaignId, message, subscriptionUi })
                return
            }

            setSubmitError(message)
        },
        onCompleted(result) {
            const created = result?.createCampaignWithSubscriptions
            if (!created) return
            handleCreationResult(created, 'License created!')
        },
    })

    // The recovery snapshot (error message, chosen plans) was handed down as this mount's initial
    // state — once read, it must not resurrect if the customer later revisits this same draft URL.
    useEffect(() => {
        onRecoveryConsumed?.()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    const handleCampaignNext = useCallback(() => {
        setSubmitError(null)
        setError([])
        setSubscriptionCanSubmit(false)
        setSubscriptionMounted(true)
        setStep(2)
    }, [])

    const handleSubscriptionFooterStateChange = useCallback(({ canSubmit, handleSubmit }) => {
        setSubscriptionCanSubmit(canSubmit)
        setSubscriptionSubmitAction(() => handleSubmit)
    }, [])

    const handleCampaignFooterStateChange = useCallback(
        ({ onSubmit, onCancel, submitDisabled, cancelDisabled, submitLabel }) => {
            setCampaignSubmitAction(() => onSubmit)
            setCampaignCancelAction(() => onCancel)
            setCampaignSubmitDisabled(submitDisabled)
            setCampaignCancelDisabled(cancelDisabled)
            setCampaignSubmitLabel(submitLabel)
        },
        [],
    )

    const submitLock = useActionLock()
    const handleSubscriptionSubmit = useCallback(
        (inputs, uiSnapshot) =>
            submitLock(async () => {
                setSubscriptionUi(uiSnapshot)
                setSubmitError(null)

                if (draftCampaignId) {
                    await completeDraftCampaign({
                        variables: { input: { campaignId: draftCampaignId, ...inputs } },
                    })
                    return
                }

                await createCampaignWithSubscriptions({
                    variables: {
                        userId: user.id,
                        input: {
                            ...campaignInputFromData(updatedData),
                            ...inputs,
                        },
                    },
                })
            }),
        [completeDraftCampaign, createCampaignWithSubscriptions, draftCampaignId, submitLock, updatedData, user.id],
    )

    const updateData = (data) => {
        setUpdatedData(data)
    }

    useEffect(() => {
        if (step !== 1) return
        if (!guidedRef.current && guide === 'on') {
            showNotification(
                <div>
                    <p>Please enter the details for your license in the fields below.</p>
                    <p>
                        If you need help, hover your mouse over the question mark icon to read the tooltip information.
                    </p>
                </div>,
                'info',
                false,
                'Continue',
                () => focus('.campaign-step-1'),
                false,
            )
            guidedRef.current = true
        }
    }, [step, guide, focus])

    return (
        <Fragment>
            <ToastContainer />
            <Card className="page-card">
                <Card.Header className="d-flex justify-content-between">
                    <div className="d-flex align-items-center justify-content-center">
                        <i className="icon text-gray-200 me-2">
                            {partialSuccess ? (
                                <SubscriptionsIcon size={32} />
                            ) : step === 1 ? (
                                <PlusIcon size={32} />
                            ) : (
                                <SubscriptionsIcon size={32} />
                            )}
                        </i>
                        <h5 className="title mb-0 text-white">
                            {partialSuccess ? 'License Set Up' : step === 1 ? 'Add License' : 'Select Subscriptions'}
                        </h5>
                    </div>
                </Card.Header>
                <Card.Body className="tw:flex tw:flex-col tw:h-full">
                    {partialSuccess ? (
                        <div className="tw:flex tw:flex-col tw:gap-4 tw:p-6">
                            <p className="tw:text-white tw:text-lg tw:font-semibold">
                                Your license was set up, but some services couldn't be charged
                            </p>
                            <p className="tw:text-sm tw:text-[#97AFCD]">
                                {partialSuccess.systemStatus === 'ACTIVE'
                                    ? 'The license itself is active. '
                                    : 'The license was created, but its payment still needs to be completed. '}
                                The services below were not added — you can add them from Subscriptions once a
                                working payment method is on file.
                            </p>
                            <ul className="tw:list-disc tw:pl-5 tw:text-sm tw:text-[#97AFCD] tw:flex tw:flex-col tw:gap-1">
                                {partialSuccess.failedSubscriptions.map((failed, index) => (
                                    <li key={`${failed.type}-${index}`}>
                                        <span className="tw:text-white tw:font-medium">
                                            {serviceLabel(failed.type)}:
                                        </span>{' '}
                                        {failed.message}
                                    </li>
                                ))}
                            </ul>
                            <p className="tw:text-sm">
                                <Link
                                    to={`/subscriptions?license=${encodeURIComponent(partialSuccess.campaignId)}`}
                                    className="tw:underline tw:text-[#97AFCD] hover:tw:text-white"
                                >
                                    Add these services from Subscriptions
                                </Link>
                            </p>
                        </div>
                    ) : (
                        <>
                            <div hidden={step !== 1} className="new-user-info">
                                <CampaignForm
                                    updateData={updateData}
                                    callback={handleCampaignNext}
                                    errors={errors}
                                    mutation={{ loading: false }}
                                    guidance={guide === 'on'}
                                    submitLabel={['Next', 'Next…']}
                                    onFooterStateChange={handleCampaignFooterStateChange}
                                />
                            </div>
                            {subscriptionMounted && (
                                <div hidden={step !== 2} className="tw:relative tw:flex tw:flex-col tw:flex-1 tw:h-full">
                                    {submitError && (
                                        <p className="tw:text-[#E57373] tw:text-sm tw:px-6 tw:pt-4">{submitError}</p>
                                    )}
                                    <SubscriptionStep
                                        initialPaymentMethodId={paymentMethodId}
                                        initialUi={subscriptionUi}
                                        submitting={(createMutation.loading || completeMutation.loading)}
                                        onSubmit={handleSubscriptionSubmit}
                                        onFooterStateChange={handleSubscriptionFooterStateChange}
                                    />
                                </div>
                            )}
                        </>
                    )}
                </Card.Body>
                <Card.Footer>
                    <div className="d-flex justify-content-end gap-3 py-2">
                        {partialSuccess ? (
                            <Button onClick={() => navigate('/campaigns', { state: { refetch: true } })}>
                                Go to Licenses
                            </Button>
                        ) : step === 1 ? (
                            <>
                                <Button
                                    variant="outline"
                                    onClick={() => campaignCancelAction?.()}
                                    disabled={campaignCancelDisabled}
                                >
                                    Cancel
                                </Button>
                                <Button
                                    onClick={() => campaignSubmitAction?.()}
                                    disabled={campaignSubmitDisabled}
                                    className="campaign-step-13"
                                >
                                    {campaignSubmitLabel}
                                </Button>
                            </>
                        ) : null}
                        {!partialSuccess && step === 2 ? (
                            <>
                                {!completingDraft && (
                                    <Button
                                        variant="outline"
                                        onClick={() => setStep(1)}
                                        disabled={createMutation.loading || completeMutation.loading}
                                    >
                                        Back
                                    </Button>
                                )}
                                <Button
                                    onClick={() => subscriptionSubmitAction?.()}
                                    disabled={!subscriptionCanSubmit || (createMutation.loading || completeMutation.loading)}
                                >
                                    {createMutation.loading || completeMutation.loading
                                        ? 'Saving…'
                                        : completingDraft
                                          ? 'Complete License'
                                          : 'Create'}
                                </Button>
                            </>
                        ) : null}
                    </div>
                </Card.Footer>
            </Card>
        </Fragment>
    )
    },
)

AddCampaignInner.displayName = 'AddCampaignInner'
export default AddCampaign
